import React, { useEffect, useState } from 'react';
import Lightbox, { useLightboxState } from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';

/**
 * Custom toolbar button — rotates the currently visible slide 90° clockwise.
 * Uses YARL's internal state hook so it can read the active slide index.
 * Rotation is client-side only; persistence still lives in the grid (Q7).
 */
const RotateButton = ({ onRotate }) => {
  const { currentIndex } = useLightboxState();
  return (
    <button
      type="button"
      className="yarl__button"
      onClick={() => onRotate(currentIndex)}
      title="Rotate 90°"
      aria-label="Rotate 90°"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="24"
        height="24"
        fill="currentColor"
      >
        <path d="M15.55 5.55 11 1v3.07A8 8 0 1 0 19.94 13H17.9A6 6 0 1 1 12 6.06V9z" />
      </svg>
    </button>
  );
};

/**
 * Shared photo lightbox used by:
 *   - PropertyPhotos (admin grid)
 *   - MonthlyInspectionEdit (per-report inspection grid)
 *   - SpaceShow (public listing)
 *
 * Features (per PHOTO_MODULE_CLIENT_QUESTIONS.md):
 *   - Prev/next, image count, enlarged view (required)
 *   - Pinch/scroll zoom, ←/→/Esc keyboard, fullscreen (Q4a)
 *   - Touch gestures via the library — works on mobile + desktop (Q4b)
 *   - "Image no longer available" placeholder when a slide fails to load,
 *     viewer stays open, prev/next still works (Q5)
 *   - View-only — no edit controls inside the viewer (Q7)
 */
const PhotoGallery = ({
  open,
  close,
  slides,
  index = 0,
  // Controlled-mode rotation. Pass both to let the parent own per-slide
  // rotation state (e.g. PropertyPhotos syncs lightbox rotation with the
  // editable grid thumbnails). If `onRotate` is not provided the gallery
  // manages rotation internally and resets on close.
  rotations: controlledRotations,
  onRotate: controlledOnRotate,
}) => {
  // Track which slide indices failed to load so we can render a placeholder
  // in-place instead of a broken image icon.
  const [erroredIndices, setErroredIndices] = useState(() => new Set());

  // Per-slide rotation in degrees (0/90/180/270).
  const [internalRotations, setInternalRotations] = useState({});

  const isControlled = typeof controlledOnRotate === 'function';
  const rotations = isControlled ? (controlledRotations || {}) : internalRotations;

  const handleRotate = (slideIndex) => {
    if (isControlled) {
      controlledOnRotate(slideIndex);
    } else {
      setInternalRotations((prev) => ({
        ...prev,
        [slideIndex]: ((prev[slideIndex] || 0) + 90) % 360,
      }));
    }
  };

  useEffect(() => {
    if (!open) return;

    setErroredIndices(new Set());
    // Only reset rotations in uncontrolled mode — controlled rotations live
    // in the parent and persist across open/close cycles by design.
    if (!isControlled) {
      setInternalRotations({});
    }

    // Preload each image to detect deletions / 403s before the user navigates.
    // S3 presigned URLs expire after an hour, which makes onerror-on-render
    // a real case in the field, not just a theoretical one.
    slides.forEach((slide, i) => {
      if (!slide?.src) {
        setErroredIndices((prev) => {
          const next = new Set(prev);
          next.add(i);
          return next;
        });
        return;
      }

      const probe = new Image();
      probe.onerror = () => {
        setErroredIndices((prev) => {
          const next = new Set(prev);
          next.add(i);
          return next;
        });
      };
      probe.src = slide.src;
    });
  }, [open, slides]);

  if (!slides || slides.length === 0) return null;

  const safeIndex = Math.min(Math.max(index, 0), slides.length - 1);

  return (
    <Lightbox
      open={open}
      close={close}
      slides={slides}
      index={safeIndex}
      plugins={[Zoom, Fullscreen, Counter]}
      carousel={{
        finite: slides.length <= 1,
        preload: 1,
      }}
      animation={{ fade: 250, swipe: 300 }}
      controller={{ closeOnBackdropClick: true }}
      counter={{ container: { style: { top: 'unset', bottom: 0 } } }}
      toolbar={{
        buttons: [
          <RotateButton key="rotate" onRotate={handleRotate} />,
          'close',
        ],
      }}
      zoom={{
        maxZoomPixelRatio: 4,
        zoomInMultiplier: 2,
        doubleTapDelay: 300,
        doubleClickDelay: 300,
        scrollToZoom: true,
      }}
      render={{
        slide: ({ slide, rect }) => {
          const idx = slides.indexOf(slide);

          if (erroredIndices.has(idx)) {
            return (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  color: '#bbb',
                  padding: '40px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '64px', lineHeight: 1, opacity: 0.6 }}>📷</div>
                <div style={{ fontSize: '18px', fontWeight: 500 }}>
                  Image no longer available
                </div>
                {slide.title && (
                  <div style={{ fontSize: '14px', opacity: 0.6 }}>{slide.title}</div>
                )}
              </div>
            );
          }

          const rotation = rotations[idx] || 0;
          if (rotation === 0) {
            // Fall through to default rendering — keeps Zoom plugin integration.
            return undefined;
          }

          // Rotated slide — render manually. When the image is rotated 90/270,
          // its visual width/height swap, so we cap max-width / max-height with
          // the rect's other dimension so the rotated image still fits the viewport.
          const isSideways = rotation % 180 !== 0;
          return (
            <div
              style={{
                width: rect.width,
                height: rect.height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={slide.src}
                alt={slide.title || ''}
                style={{
                  maxWidth: isSideways ? rect.height : rect.width,
                  maxHeight: isSideways ? rect.width : rect.height,
                  transform: `rotate(${rotation}deg)`,
                  transition: 'transform 0.25s ease',
                  objectFit: 'contain',
                }}
              />
            </div>
          );
        },
      }}
    />
  );
};

export default PhotoGallery;
